import { PureComponent } from 'react'
import * as styles from 'components/features/socialFeed/FeedPost/FeedPostComment.styl'
import * as cn from 'classnames'
import { FeedPostText } from 'components/features/socialFeed/FeedPost/FeedPostText'
import { ActionIcon } from 'components/features/pfm/ActionMenu/ActionIcon'
import Anchor from 'components/atoms/navigation/Anchor'
import SvgIcon from 'components/atoms/SvgIcon'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import EditSvg from 'icons/pencil.svg'
import FollowAuthorSvg from 'icons/follow-author.svg'
import UnfollowAuthorSvg from 'icons/unfollow-author.svg'
import CrossSvg from 'icons/cross.svg'
import ReportSvg from 'icons/report.svg'
import { getFeedCommentAnchorId } from 'helpers/feedHelpers'
import { CommentUid, FeedUserUid, Comment } from 'interfaces/feed'
import { Compose } from 'components/features/socialFeed/Compose/Compose'
import AttachmentsBlock from 'components/features/socialFeed/FeedPost/AttachmentsBlock'
import FeedUserInfoContainer from 'components/features/socialFeed/FeedUserInfo/FeedUserInfoContainer'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { ImageURLs } from 'interfaces/media'
import { CommentLikeButton } from 'components/features/pfm/SegmentButton/CommentLikeButton'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { getEditCommentComposeId, getReplyComposeId } from 'helpers/composeHelpers'
import { SocialProfileEnsure } from 'components/features/socialFeed/SocialProfileEnsure/SocialProfileEnsure'

export type FeedPostCommentStateProps = {
  isLikeLoading: boolean
  isAuthorFollowLoading: boolean
  isCommentEditing: boolean

  authorUid: FeedUserUid
  authorName: string
  comment: Comment
  images: ImageURLs[]

  isOwn: boolean
  isAuthorFollowed: boolean
  isPostAuthor: boolean
  isAuthenticated: boolean
  hasReplies: boolean
  isRepliesHidden: boolean
  hasReplyCompose: boolean
}

export type FeedPostCommentDispatchProps = {
  onToggleCommentLike: (isLiked: boolean, authorUid: FeedUserUid) => void
  onToggleFollowAuthor: (toFollow: boolean, authorUid: FeedUserUid) => void
  onToggleEditComment: () => void
  onCancelEditComment: () => void
  onSendReport: () => void
  onDeleteComment: (comment: Comment) => void
  onReplyComment: () => void
  onReplyCancel: () => void
  onToggleReplies: (isShown: boolean) => void
}

export type FeedPostCommentOwnProps = {
  commentUid: CommentUid
  parentCommentUid: CommentUid
}

type FeedPostCommentProps =
  & FeedPostCommentStateProps
  & FeedPostCommentDispatchProps
  & FeedPostCommentOwnProps

type FeedPostCommentState = {
  isActionsMenuShown: boolean
}

export class FeedPostComment extends PureComponent<FeedPostCommentProps, FeedPostCommentState> {
  state: FeedPostCommentState = {
    isActionsMenuShown: false,
  }

  private composeId = getEditCommentComposeId(this.props.commentUid)

  render () {
    const {
      commentUid,
    } = this.props

    return (
      <Anchor placement={ getFeedCommentAnchorId(commentUid) }>
        { this.renderComment() }
      </Anchor>
    )
  }

  private renderComment () {
    const {
      isCommentEditing,
      comment,
      images,
      isOwn,
      isPostAuthor,
      hasReplies,
      isRepliesHidden,
      hasReplyCompose,
      onReplyCancel,
      isAuthenticated,
      isLikeLoading,
      commentUid,
    } = this.props

    const {
      isActionsMenuShown,
    } = this.state

    const nameSection = (
      <div className={ styles.nameSection }>
        <FeedUserInfoContainer
          userUid={ comment.authorUid }
          created={ comment.created }
          isEdited={ !!comment.modified }
          matchingPeergroup={ comment.peergroup }
          mode='comment'
          isOwn={ isOwn }
          isPostAuthor={ isPostAuthor }
        />
      </div>
    )

    const actions = (
      <div className={ styles.actions }>
        { isAuthenticated && (
          <div className={ styles.actionMenuContainer }>
            <button
              className={ styles.actionMenuButton }
              onClick={ this.showActionsMenu }
            >
              <ActionIcon />
            </button>

            <ActionMenu
              className={ cn(styles.actionMenu, isActionsMenuShown && styles.actionMenuShown) }
              onHide={ this.hideActionsMenu }
            >
              { isOwn && (
                <>
                  {
                    !isCommentEditing &&
                    <AnalyticsClick
                      category='module-feed_post_comment'
                      action='click-edit'
                    >
                      <ActionMenuItem
                        icon={ <SvgIcon src={ EditSvg } /> }
                        onClick={ this.handleToggleEditComment }
                      >
                        Edit
                      </ActionMenuItem>
                    </AnalyticsClick>
                  }
                  <AnalyticsClick
                    category='module-feed_post_comment'
                    action='click-delete'
                  >
                    <ActionMenuItem
                      icon={ <SvgIcon src={ CrossSvg } /> }
                      onClick={ this.handleDeleteComment }
                    >
                      Delete
                    </ActionMenuItem>
                  </AnalyticsClick>
                </>
              )}
              { !isOwn && (
                <>
                  { this.renderFollowItem() }
                  { this.renderReportItem() }
                </>
              )}
            </ActionMenu>
          </div>
        )}
      </div>
    )

    return (
      <div className={ styles.feedPostComment }>
        <header className={ styles.header }>
          { nameSection }
          { actions }
        </header>

        <div className={ styles.content }>
          { !isCommentEditing &&
            <>
              <div className={ styles.text }>
                <FeedPostText
                  text={ comment.text }
                  mode='comment'
                  mentions={ comment.mentions }
                />
              </div>
              <AttachmentsBlock images={ images } links={ comment.links } mode='comment' />
              <div className={ styles.bottomActionsRow }>
                <div className={ styles.replyActions }>
                  { hasReplies && (
                    <>
                      { !isRepliesHidden && (
                        <div className={ styles.replyAction } onClick={ this.handleHideReplies }>Hide Replies</div>
                      )}
                      { isRepliesHidden && (
                        <div className={ styles.replyAction } onClick={ this.handleShowReplies }>Show Replies</div>
                      )}
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                    </>
                  )}
                  { !hasReplyCompose && (
                    <SocialProfileEnsure composeId={ getReplyComposeId(commentUid) }>
                      <FeedLinkLoggedOut type='post' postUid={ comment.postUid }>
                        {
                          (isUserAuthenticated) => {
                            const handleClick = isUserAuthenticated ? this.handleReply : undefined

                            return (
                              <div
                                className={ styles.replyAction }
                                onClick={ handleClick }
                              >
                                Reply
                              </div>
                            )
                          }
                        }
                      </FeedLinkLoggedOut>
                    </SocialProfileEnsure>
                  )}
                  { hasReplyCompose && (
                    <div className={ styles.replyAction } onClick={ onReplyCancel }>Cancel</div>
                  )}
                </div>

                <div className={ styles.likeButtonWrapper }>
                  <FeedLinkLoggedOut type='post' postUid={ comment.postUid }>
                    {
                      (isUserAuthenticated) => {
                        const handleClick = isUserAuthenticated ? this.toggleLike : undefined

                        return (
                          <CommentLikeButton
                            disabled={ isLikeLoading }
                            pressed={ comment.is_liked }
                            counter={ comment.likes_count }
                            onClick={ handleClick }
                          />
                        )
                      }
                    }
                  </FeedLinkLoggedOut>
                </div>
              </div>
            </>}
          { isCommentEditing && this.renderEditing() }
        </div>
      </div>
    )
  }

  private renderFollowItem () {
    const {
      isAuthorFollowed,
      isAuthorFollowLoading,
      authorName,
      comment,
    } = this.props

    const followActionTitle = `${ isAuthorFollowed ? 'Unfollow' : 'Follow' }`

    return (
      <AnalyticsClick
        category='module-feed_post_comment'
        action='toggle-follow_author'
        label={ isAuthorFollowed ? 'unfollow' : 'follow' }
      >
        <FeedLinkLoggedOut type='post' postUid={ comment.postUid }>
          {
            (isAuthenticated) => {
              const icon = (
                <SvgIcon src={ isAuthorFollowed ? UnfollowAuthorSvg : FollowAuthorSvg } />
              )
              const handleClick = isAuthorFollowLoading || !isAuthenticated
                ? undefined
                : this.toggleAuthorFollowing

              return (
                <ActionMenuItem
                  icon={ icon }
                  onClick={ handleClick }
                  hint={ `${ followActionTitle } ${ authorName }` }
                >
                  { followActionTitle }
                  <span style={{ textTransform: 'none' }}> { authorName }</span>
                </ActionMenuItem>
              )
            }
          }
        </FeedLinkLoggedOut>
      </AnalyticsClick>
    )
  }

  private renderReportItem () {
    const {
      comment,
    } = this.props

    return (
      <AnalyticsClick
        category='module-feed_post_comment'
        action='toggle-report'
        label={ comment.is_reported ? 'unreport' : 'report' }
      >
        <FeedLinkLoggedOut type='post' postUid={ comment.postUid }>
          {
            (isAuthenticated) => {
              const icon = (
                <SvgIcon src={ ReportSvg } />
              )
              const handleClick = comment.is_reported || !isAuthenticated
                ? undefined
                : this.handleSendReport

              return (
                <ActionMenuItem
                  icon={ icon }
                  onClick={ handleClick }
                  isDisabled={ comment.is_reported }
                >
                  { comment.is_reported ? 'Reported' : 'Report' }
                </ActionMenuItem>
              )
            }
          }
        </FeedLinkLoggedOut>
      </AnalyticsClick>
    )
  }

  private renderEditing () {
    const { comment, onCancelEditComment } = this.props
    return (
      <div className={ styles.compose }>
        <Compose
          placeholder='Write a comment...'
          isEdit
          composeId={ this.composeId }
          postUid={ comment.postUid }
          commentUid={ comment.uid }
          onDone={ onCancelEditComment }
          focusOnMount
        />
      </div>
    )
  }

  private showActionsMenu = () => {
    this.setState({ isActionsMenuShown: true })
  }

  private hideActionsMenu = () => {
    this.setState({ isActionsMenuShown: false })
  }

  private toggleAuthorFollowing = () => {
    const { isAuthorFollowed, onToggleFollowAuthor, comment } = this.props
    this.hideActionsMenu()
    onToggleFollowAuthor(!isAuthorFollowed, comment.authorUid)
  }

  private toggleLike = () => {
    const { comment, authorUid, onToggleCommentLike } = this.props
    onToggleCommentLike(!comment.is_liked, authorUid)
  }

  private handleHideReplies = () => {
    this.props.onToggleReplies(true)
  }

  private handleShowReplies = () => {
    this.props.onToggleReplies(false)
  }

  private handleReply = () => {
    const { onReplyComment, isRepliesHidden } = this.props
    if (isRepliesHidden) {
      this.handleShowReplies()
    }
    onReplyComment()
  }

  private handleToggleEditComment = () => {
    const { onToggleEditComment } = this.props
    this.hideActionsMenu()
    onToggleEditComment()
  }

  private handleDeleteComment = () => {
    const { comment, onDeleteComment } = this.props
    this.hideActionsMenu()
    onDeleteComment(comment)
  }

  private handleSendReport = () => {
    const { onSendReport } = this.props
    this.hideActionsMenu()
    onSendReport()
  }

}
